import { createSlice, type PayloadAction } from '@reduxjs/toolkit'

/** A single invited participant record. */
export interface Invitation {
  id: string
  eventId: string
  email: string
  /** Optional personalised message to include in the invitation email.
   *  Absent or empty = "default" template. */
  customMessage?: string
  /**
   * `'invited'`    — invitation sent, not yet registered.
   * `'registered'` — participant has since completed registration (reserved for next task).
   */
  status: 'invited' | 'registered'
  /** ISO datetime string WITH UTC offset, e.g. 2026-06-15T07:32:00+07:00.
   *  Updated every time an invitation is (re-)sent. */
  lastInvitedAt: string
}

interface InvitationsState {
  list: Invitation[]
}

const initialState: InvitationsState = { list: [] }

const invitationsSlice = createSlice({
  name: 'invitations',
  initialState,
  reducers: {
    /** Add one or more new invitations (from the Invite modal). */
    addInvitations(state, action: PayloadAction<Invitation[]>) {
      state.list.push(...action.payload)
    },

    /** Update lastInvitedAt to now for a set of invitation ids (re-send). */
    resendInvitations(state, action: PayloadAction<{ ids: string[]; now: string }>) {
      const { ids, now } = action.payload
      const idSet = new Set(ids)
      for (const inv of state.list) {
        if (idSet.has(inv.id)) {
          inv.lastInvitedAt = now
        }
      }
    },

    /** Mark one or more invitations as 'registered' (reserved for participant lifecycle task). */
    markAsRegistered(state, action: PayloadAction<string[]>) {
      const idSet = new Set(action.payload)
      for (const inv of state.list) {
        if (idSet.has(inv.id)) {
          inv.status = 'registered'
        }
      }
    },

    /** Permanently remove invitations by id. */
    deleteInvitations(state, action: PayloadAction<string[]>) {
      const idSet = new Set(action.payload)
      state.list = state.list.filter((inv) => !idSet.has(inv.id))
    },
  },
})

export const { addInvitations, resendInvitations, markAsRegistered, deleteInvitations } =
  invitationsSlice.actions
export default invitationsSlice.reducer
